import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Layout } from './components/layout/Layout';
import { useAuthStore } from './stores/authStore';
import { useSSEStore } from './stores/sseStore';
import { loadTheme, getCurrentTheme } from './themes';
import Dashboard from './pages/dashboard';
import OrdersPage from './pages/orders';
import Login from './pages/login';
import Settings from './pages/settings';
import HealthOverview from './pages/admin/HealthOverview';
import ReviewsPage from './pages/admin/ReviewsPage';
import PricingLossesPage from './pages/admin/PricingLossesPage';
import PriceMonitorRunsPage from './pages/admin/PriceMonitorRunsPage';
import CatalogPage from './pages/catalog';
import CatalogDetailPage from './pages/catalog/CatalogDetailPage';
import PurchasesPage from './pages/purchases';
import SalesPage from './pages/sales';
import FinancePage from './pages/finance';
import MercadoLibrePage from './pages/mercadolibre';
import AmazonPage from './pages/amazon';
import AIPage from './pages/ai';
import WarehousePage from './pages/warehouse';
import ReportsPage from './pages/reports';
import LogisticsPage from './pages/logistics';
import ContactsPage from './pages/contacts';
import PublisherPage from './pages/publisher';
import QuestionsPage from './pages/questions';
import MessagesPage from './pages/messages';
import AssistantPage from './pages/assistant';
import QuestionMiniApp from './pages/telegram/QuestionMiniApp';
import ReleaseMiniApp from './pages/telegram/ReleaseMiniApp';
import ClaimMobilePage from './pages/claim-mobile';
import { AREA_OF_ROUTE, ROUTE_ANY_CAPS, RequireCap, NoAccess, useCan, firstAllowedRoute } from './lib/rbac';

/** El inicio es especial: quien no tenga el tablero NO puede quedar encerrado ahí
 *  (el login y las redirecciones mandan siempre a "/"), así que lo derivamos a su primera
 *  pantalla disponible. Si no tiene ninguna, mostramos el aviso (sin redirigir: evitaría un bucle). */
function HomeRoute({ children }: { children: React.ReactNode }) {
  const can = useCan();
  if (can(AREA_OF_ROUTE['/'])) return <>{children}</>;
  const to = firstAllowedRoute(can);
  return to ? <Navigate to={to} replace /> : <NoAccess />;
}

/** Envuelve la pantalla con la guardia de capacidad de su ruta.
 *  Rutas de ROUTE_ANY_CAPS (ajustes, contactos): entra con CUALQUIERA de sus pestañas.
 *  La raíz usa HomeRoute (derivación en vez de aviso). */
const guard = (path: string, el: React.ReactNode) =>
  path === '/' ? (
    <HomeRoute>{el}</HomeRoute>
  ) : (
    <RequireCap cap={ROUTE_ANY_CAPS[path] ? undefined : AREA_OF_ROUTE[path]} any={ROUTE_ANY_CAPS[path]}>
      {el}
    </RequireCap>
  );

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { checkAuth, isAuthenticated } = useAuthStore();
  const { connect, disconnect } = useSSEStore();

  useEffect(() => {
    loadTheme(getCurrentTheme());
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (isAuthenticated) {
      connect();
    } else {
      disconnect();
    }
  }, [isAuthenticated, connect, disconnect]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* Telegram Mini App routes — no auth guard, auth via Telegram initData */}
        <Route path="/tg/question/:id" element={<QuestionMiniApp />} />
        <Route path="/tg/questions" element={<QuestionMiniApp />} />
        <Route path="/tg/release/:batchId" element={<ReleaseMiniApp />} />
        {/* Mini-app móvil de reclamos — sin auth guard, acceso por token firmado del enlace */}
        <Route path="/c/:token" element={<ClaimMobilePage />} />
        <Route
          element={
            <AuthGuard>
              <Layout />
            </AuthGuard>
          }
        >
          <Route path="/" element={guard('/', <Dashboard />)} />
          <Route path="/orders" element={guard('/orders', <OrdersPage />)} />
          <Route path="/questions" element={guard('/questions', <QuestionsPage />)} />
          <Route path="/messages" element={guard('/messages', <MessagesPage />)} />
          <Route path="/catalog" element={guard('/catalog', <CatalogPage />)} />
          <Route path="/catalog/:itemId" element={guard('/catalog/:itemId', <CatalogDetailPage />)} />
          <Route path="/suppliers" element={guard('/suppliers', <Navigate to="/purchases?tab=suppliers" replace />)} />
          <Route path="/purchases" element={guard('/purchases', <PurchasesPage />)} />
          <Route path="/sales" element={guard('/sales', <SalesPage />)} />
          <Route path="/finance" element={guard('/finance', <FinancePage />)} />
          <Route path="/reports" element={guard('/reports', <ReportsPage />)} />
          <Route path="/mercadolibre" element={guard('/mercadolibre', <MercadoLibrePage />)} />
          <Route path="/amazon" element={guard('/amazon', <AmazonPage />)} />
          <Route path="/warehouse" element={guard('/warehouse', <WarehousePage />)} />
          <Route path="/ai" element={guard('/ai', <AIPage />)} />
          <Route path="/contacts" element={guard('/contacts', <ContactsPage />)} />
          <Route path="/logistics" element={guard('/logistics', <LogisticsPage />)} />
          <Route path="/publisher" element={guard('/publisher', <PublisherPage />)} />
          <Route path="/settings" element={guard('/settings', <Settings />)} />
          <Route path="/admin/health" element={guard('/admin/health', <HealthOverview />)} />
          <Route path="/admin/reviews" element={guard('/admin/reviews', <ReviewsPage />)} />
          <Route path="/admin/pricing-losses" element={guard('/admin/pricing-losses', <PricingLossesPage />)} />
          <Route path="/admin/price-monitor" element={guard('/admin/price-monitor', <PriceMonitorRunsPage />)} />
          <Route path="/assistant" element={guard('/assistant', <AssistantPage />)} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
